import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Alert from 'flarum/common/components/Alert';
import icon from 'flarum/common/helpers/icon';
import MentionAutocomplete from './MentionAutocomplete';
import MentionSearch from '../mentionSearch';
import { checkImage, nsfwEnabled } from '../nsfwCheck';
import { canUploadVideo, videoLimits, checkVideo } from '../videoCheck';

const GRADIENTS = [
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)',
  'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
  'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',
];

/**
 * Fullscreen modal to create a new story.
 *
 * Props:
 *   onclose   – callback
 *   oncreated – callback(story, canCreate)
 */
/** Icon per visibility level, mirroring what each one means. */
const VISIBILITY_ICONS = {
  public: 'fas fa-globe',
  members: 'fas fa-users',
  followers: 'fas fa-user-check',
};

export default class StoryCreator extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    
    this.type         = 'text'; // 'text' | 'image' | 'video'
    this.content      = '';
    this.bgGradient   = GRADIENTS[0];
    this.imageFile    = null;
    this.imagePreview = null;
    // Object URL of the picked video; revoked when it is replaced or dropped.
    this.videoFile    = null;
    this.videoPreview = null;
    this.videoError   = null;
    // Upload progress in percent while a video is being sent, else null.
    this.uploadProgress = null;
    // Why the last share failed. Shown inside the composer: Flarum's own
    // alerts sit below this fullscreen layer and would never be seen.
    this.error        = null;
    this.linkUrl      = '';
    // Conservative default: nothing is exposed to guests by accident.
    this.visibility   = 'members';
    this.linkLabel    = '';
    this.loading      = false;
    // Which settings panel is expanded, if any.
    this.panel        = null;
    // Caret position, so the @mention list knows what is being typed.
    this.cursor       = 0;
    // Nudity screening state for the selected image.
    // Optional poll. Empty options are dropped on submit, so starting with
    // two blanks costs nothing and shows the shape straight away.
    this.pollQuestion = '';
    // Each option carries an id of its own. The list used to be plain strings
    // keyed by index, and an index is not a stable identity: on redraw Mithril
    // matched an input to a different option than the one it belonged to, so a
    // typed answer could land in the wrong box or be lost outright — the poll
    // then failed hasPoll() and was submitted as no poll at all. That is why it
    // worked once and not the next time.
    this.pollOptionSeq = 0;
    this.pollOptions = [this.newPollOption(), this.newPollOption()];
    this.scanning     = false;
    this.rejected     = false;
    // Search state lives here, not in the dropdown: Mithril replaces component
    // instances on redraw, so state kept inside one isn't what's on screen.
    this.mentions     = new MentionSearch(
      // Deferred: refresh() runs inside oninput, and Mithril ignores a redraw
      // requested while it is already handling one.
      () => setTimeout(() => m.redraw(), 0),
      (user) => this.chooseMention(user)
    );

    // Prevent body scroll
    document.body.classList.add('StoryCreator-open');
  }

  onremove(vnode) {
    super.onremove(vnode);
    this.clearVideo();
    document.body.classList.remove('StoryCreator-open');
  }

  view() {
    return (
      <div className="StoryCreator">
        {/* Header — close, title, share */}
        <div className="StoryCreator-header">
          <button
            className="StoryCreator-iconBtn"
            type="button"
            title={app.translator.trans('happydigital-stories.forum.cancel')}
            onclick={() => this.attrs.onclose()}
          >
            {icon('fas fa-times')}
          </button>

          <h3>{app.translator.trans('happydigital-stories.forum.add_story')}</h3>

          <Button
            className="Button Button--primary StoryCreator-share"
            loading={this.loading}
            disabled={!this.canSubmit()}
            onclick={() => this.submit()}
          >
            {this.shareLabel()}
          </Button>
        </div>

        {this.error ? (
          <div className="StoryCreator-error">
            <Alert type="error" dismissible={true} ondismiss={() => (this.error = null)}>
              {this.error}
            </Alert>
          </div>
        ) : null}

        {/* Canvas — the story as it will look */}
        <div
          className={'StoryCreator-canvas StoryCreator-canvas--' + this.type}
          style={{ background: this.type === 'text' ? this.bgGradient : null }}
        >
          {this.canvas()}
        </div>

        {/* While a mention is being typed the suggestions take the tool bar's
            place: on a phone the keyboard already owns half the screen, and
            overlaying the list on the canvas covered the text being written. */}
        {this.mentions.open
          ? MentionAutocomplete.component({
              results: this.mentions.results,
              loading: this.mentions.loading,
              index: this.mentions.index,
              onchoose: (user) => this.chooseMention(user),
            })
          : null}

        {/* Tool bar — everything else lives behind these */}
        <div className={'StoryCreator-tools' + (this.mentions.open ? ' is-hidden' : '')}>
          {this.toolButton('text', 'fas fa-font', 'type_text', () => this.setType('text'))}
          {this.toolButton('image', 'fas fa-image', 'type_image', () => this.setType('image'))}
          {canUploadVideo()
            ? this.toolButton('video', 'fas fa-video', 'type_video', () => this.setType('video'))
            : null}

          <span className="StoryCreator-toolDivider" />

          {/* Poll comes first of the three. The row scrolls sideways on a phone
              and the last button sits off-screen behind a hidden scrollbar, so
              whatever is last goes unnoticed — which is what happened to the
              poll. Audience and link are options people look for; a poll is one
              they have to be shown.

              Text-only: over a picture the box covers the very thing being
              shown, so it is not offered there at all. */}
          {this.type === 'text'
            ? this.panelButton('poll', 'fas fa-poll', 'poll_label', this.hasPoll())
            : null}
          {this.panelButton(
            'visibility',
            VISIBILITY_ICONS[this.visibility],
            'visibility_label',
            false,
            true
          )}
          {this.panelButton('link', 'fas fa-link', 'link_label', this.linkUrl.trim() !== '', true)}
        </div>

        {/* Expanding panel for the selected tool */}
        {this.panel === 'visibility' ? this.visibilityPanel() : null}
        {this.panel === 'link' ? this.linkPanel() : null}
        {this.panel === 'poll' ? this.pollPanel() : null}
      </div>
    );
  }

  /**
   * Share button text. Once the bytes are sent the server still passes the
   * video on to mutluresim, so 100% reads as processing rather than done.
   */
  shareLabel() {
    if (this.uploadProgress === null) {
      return app.translator.trans('happydigital-stories.forum.share');
    }

    if (this.uploadProgress >= 100) {
      return app.translator.trans('happydigital-stories.forum.video_processing');
    }

    return app.translator.trans('happydigital-stories.forum.video_uploading', { percent: this.uploadProgress });
  }

  canvas() {
    if (this.type === 'text') return this.textCanvas();
    if (this.type === 'video') return this.videoCanvas();

    return this.imageCanvas();
  }

  /** The text story canvas: the words, then the colour strip. */
  textCanvas() {
    // A single wrapper rather than an array: Mithril matches array children by
    // position, and a keyless list let it recreate the textarea on redraw —
    // which dropped whatever had just been typed and lost focus.
    return (
      <div className="StoryCreator-textCanvas">
      <div className="StoryCreator-textWrap">
        <textarea
          className="StoryCreator-text"
          placeholder={app.translator.trans('happydigital-stories.forum.enter_text')}
          value={this.content}
          maxlength="500"
          oninput={(e) => {
            this.content = e.target.value;
            this.cursor = e.target.selectionStart;
            this.mentions.refresh(this.content, this.cursor);
          }}
          onclick={(e) => this.syncCursor(e)}
          onkeyup={(e) => this.syncCursor(e)}
          onkeydown={(e) => {
            // Let the suggestion list claim arrows/enter while it is open.
            if (this.mentions.onkeydown(e)) e.preventDefault();
          }}
        />
      </div>
      <div className="StoryCreator-counter">{this.content.length}/500</div>
      <div className={'StoryCreator-gradients' + (this.mentions.open ? ' is-hidden' : '')}>
        {GRADIENTS.map((grad) => (
          <button
            className={'StoryCreator-gradient' + (this.bgGradient === grad ? ' is-active' : '')}
            type="button"
            style={{ background: grad }}
            onclick={() => (this.bgGradient = grad)}
          />
        ))}
      </div>
      </div>
    );
  }

  /** The image story canvas: a drop target, or the chosen picture. */
  imageCanvas() {
    if (this.imagePreview) {
      return (
        <div className="StoryCreator-preview">
          <img src={this.imagePreview} alt="" />

          {this.scanning ? (
            <div className="StoryCreator-scanning">
              {app.translator.trans('happydigital-stories.forum.nsfw_scanning')}
            </div>
          ) : null}
          <button
            className="StoryCreator-iconBtn StoryCreator-removeImage"
            type="button"
            title={app.translator.trans('happydigital-stories.forum.remove_image')}
            onclick={() => this.clearImage()}
          >
            {icon('fas fa-trash')}
          </button>
        </div>
      );
    }

    return (
      <label className={'StoryCreator-dropzone' + (this.rejected ? ' is-rejected' : '')}>
        {this.rejected
          ? icon('fas fa-ban', { className: 'StoryCreator-dropzoneIcon' })
          : icon('fas fa-cloud-upload-alt', { className: 'StoryCreator-dropzoneIcon' })}
        <span className="StoryCreator-dropzoneLabel">
          {this.rejected
            ? app.translator.trans('happydigital-stories.forum.nsfw_blocked')
            : app.translator.trans('happydigital-stories.forum.upload_image')}
        </span>
        <span className="StoryCreator-dropzoneHint">
          {this.rejected
            ? app.translator.trans('happydigital-stories.forum.nsfw_blocked_hint')
            : app.translator.trans('happydigital-stories.forum.upload_hint')}
        </span>
        <input
          type="file"
          accept="image/*"
          onchange={(e) => this.handleImageSelect(e)}
        />
      </label>
    );
  }

  /** The video story canvas: a drop target, or the chosen clip playing muted. */
  videoCanvas() {
    if (this.videoPreview) {
      return (
        <div className="StoryCreator-preview" key="video-preview">
          <video src={this.videoPreview} autoplay muted loop playsinline />
          <button
            className="StoryCreator-iconBtn StoryCreator-removeImage"
            type="button"
            title={app.translator.trans('happydigital-stories.forum.remove_video')}
            disabled={this.loading}
            onclick={() => this.clearVideo()}
          >
            {icon('fas fa-trash')}
          </button>
        </div>
      );
    }

    const limits = videoLimits();

    return (
      <label className={'StoryCreator-dropzone' + (this.videoError ? ' is-rejected' : '')} key="video-dropzone">
        {this.videoError
          ? icon('fas fa-ban', { className: 'StoryCreator-dropzoneIcon' })
          : icon('fas fa-video', { className: 'StoryCreator-dropzoneIcon' })}
        <span className="StoryCreator-dropzoneLabel">
          {this.videoError || app.translator.trans('happydigital-stories.forum.upload_video')}
        </span>
        <span className="StoryCreator-dropzoneHint">
          {app.translator.trans('happydigital-stories.forum.upload_video_hint', {
            mb: limits.maxMb,
            seconds: limits.maxSeconds,
          })}
        </span>
        <input
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onchange={(e) => this.handleVideoSelect(e)}
        />
      </label>
    );
  }

  /** Story type switch — text, image or video. */
  toolButton(type, iconName, key, onclick) {
    return (
      <button
        className={'StoryCreator-tool' + (this.type === type ? ' is-active' : '')}
        type="button"
        title={app.translator.trans('happydigital-stories.forum.' + key)}
        onclick={onclick}
      >
        {icon(iconName)}
        <span>{app.translator.trans('happydigital-stories.forum.' + key)}</span>
      </button>
    );
  }

  /**
   * Opens a settings panel; `filled` marks an option the user has set.
   *
   * `iconOnly` drops the label. The five buttons with labels came to more than
   * a phone's width, and the row then scrolled with a hidden scrollbar, leaving
   * the last one out of sight. Audience and link keep their icon alone so the
   * row fits on one line; the label stays in the tooltip.
   */
  panelButton(name, iconName, key, filled = false, iconOnly = false) {
    return (
      <button
        className={
          'StoryCreator-tool' +
          (this.panel === name ? ' is-open' : '') +
          (filled ? ' is-filled' : '') +
          (iconOnly ? ' StoryCreator-tool--iconOnly' : '')
        }
        type="button"
        title={app.translator.trans('happydigital-stories.forum.' + key)}
        aria-label={app.translator.trans('happydigital-stories.forum.' + key)}
        onclick={() => this.togglePanel(name)}
      >
        {icon(iconName)}
        {iconOnly ? null : (
          <span>{app.translator.trans('happydigital-stories.forum.' + key)}</span>
        )}
      </button>
    );
  }

  /** Audience picker, one row per level so the choice is visible at a glance. */
  visibilityPanel() {
    const levels = [
      ['public', VISIBILITY_ICONS.public, 'visibility_public', 'visibility_public_help'],
      ['members', VISIBILITY_ICONS.members, 'visibility_members', 'visibility_members_help'],
      ['followers', VISIBILITY_ICONS.followers, 'visibility_followers', 'visibility_followers_help'],
    ];

    return (
      <div className="StoryCreator-panel">
        {levels.map(([value, iconName, label, help]) => (
          <button
            className={'StoryCreator-option' + (this.visibility === value ? ' is-active' : '')}
            type="button"
            onclick={() => {
              this.visibility = value;
              this.panel = null;
            }}
          >
            {icon(iconName, { className: 'StoryCreator-optionIcon' })}
            <span className="StoryCreator-optionText">
              <strong>{app.translator.trans('happydigital-stories.forum.' + label)}</strong>
              <small>{app.translator.trans('happydigital-stories.forum.' + help)}</small>
            </span>
            {this.visibility === value ? icon('fas fa-check', { className: 'StoryCreator-optionCheck' }) : null}
          </button>
        ))}
      </div>
    );
  }

  /** Optional link, plus the label shown on its button. */
  linkPanel() {
    return (
      <div className="StoryCreator-panel">
        <div className="StoryCreator-field">
          <label>{app.translator.trans('happydigital-stories.forum.link_label')}</label>
          <input
            type="url"
            className="FormControl"
            placeholder={app.translator.trans('happydigital-stories.forum.link_placeholder')}
            value={this.linkUrl}
            oninput={(e) => (this.linkUrl = e.target.value)}
            maxlength="500"
          />
        </div>

        {this.linkUrl.trim() ? (
          <div className="StoryCreator-field">
            <label>{app.translator.trans('happydigital-stories.forum.link_label_field')}</label>
            <input
              type="text"
              className="FormControl"
              placeholder={app.translator.trans('happydigital-stories.forum.link_label_placeholder')}
              value={this.linkLabel}
              oninput={(e) => (this.linkLabel = e.target.value)}
              maxlength="100"
            />
          </div>
        ) : null}
      </div>
    );
  }

  /** Track the caret so the mention list knows where it is being typed. */
  syncCursor(e) {
    this.cursor = e.target.selectionStart;
    this.mentions.refresh(this.content, this.cursor);
  }

  /** Replace the half-typed mention with the chosen user. */
  chooseMention(user) {
    const mention = MentionSearch.mentionAt(this.content, this.cursor);

    if (!user || !mention) return;

    this.insertMention(user.username(), mention.start, mention.typed.length + 1);
    this.mentions.reset();
  }

  /**
   * Replace the half-typed @mention with the chosen username.
   *
   * A trailing space is added so the next word isn't swallowed into the
   * mention, and the caret is put after it.
   */
  insertMention(username, start, length) {
    const before = this.content.slice(0, start);
    const after = this.content.slice(start + length);
    // Only add the separating space when the text doesn't already have one,
    // otherwise completing mid-sentence leaves a double space behind.
    const spacer = /^\s/.test(after) ? '' : ' ';
    const inserted = '@' + username + spacer;

    this.content = before + inserted + after;
    this.cursor = start + inserted.length;

    m.redraw();

    // Restore focus and caret after the redraw replaces the DOM node.
    requestAnimationFrame(() => {
      const field = document.querySelector('.StoryCreator-text');

      if (!field) return;

      field.focus();
      field.setSelectionRange(this.cursor, this.cursor);
    });
  }

  /** A blank option with an identity that survives redraws and reordering. */
  newPollOption() {
    return { id: ++this.pollOptionSeq, text: '' };
  }

  /** The answers as the server wants them: trimmed, blanks dropped. */
  pollAnswers() {
    return this.pollOptions.map((o) => o.text.trim()).filter(Boolean);
  }

  /** Whether the poll is complete enough to be stored (mirrors the server). */
  hasPoll() {
    return (
      // Text stories only, so a poll filled in before switching to a picture
      // is not submitted along with it.
      this.type === 'text' &&
      this.pollQuestion.trim() !== '' &&
      this.pollAnswers().length >= 2
    );
  }

  /** Question plus two to four answers. */
  pollPanel() {
    return (
      <div className="StoryCreator-panel">
        <div className="StoryCreator-field">
          <label>{app.translator.trans('happydigital-stories.forum.poll_label')}</label>
          <input
            type="text"
            className="FormControl"
            placeholder={app.translator.trans('happydigital-stories.forum.poll_question_placeholder')}
            value={this.pollQuestion}
            oninput={(e) => (this.pollQuestion = e.target.value)}
            maxlength="120"
          />
        </div>

        {this.pollOptions.map((option, i) => (
          <div className="StoryCreator-field StoryCreator-pollOption" key={option.id}>
            <input
              type="text"
              className="FormControl"
              placeholder={app.translator.trans('happydigital-stories.forum.poll_option_placeholder', {
                number: i + 1,
              })}
              value={option.text}
              oninput={(e) => this.setPollOption(option.id, e.target.value)}
              maxlength="40"
            />

            {this.pollOptions.length > 2 ? (
              <button
                className="StoryCreator-iconBtn"
                type="button"
                onclick={() => this.removePollOption(option.id)}
              >
                {icon('fas fa-times')}
              </button>
            ) : null}
          </div>
        ))}

        {this.pollOptions.length < 4 ? (
          <button
            className="StoryCreator-tool"
            type="button"
            onclick={() => {
              // Replaced rather than pushed, like setPollOption: an in-place
              // mutation is the pattern Mithril can miss on redraw.
              this.pollOptions = [...this.pollOptions, this.newPollOption()];
            }}
          >
            {icon('fas fa-plus')}
            <span>{app.translator.trans('happydigital-stories.forum.poll_add_option')}</span>
          </button>
        ) : null}
      </div>
    );
  }

  setPollOption(id, value) {
    // Addressed by id, not position: replacing by index rewrote the wrong
    // option once the list had been reordered or one removed.
    this.pollOptions = this.pollOptions.map((o) =>
      o.id === id ? { ...o, text: value } : o
    );
  }

  removePollOption(id) {
    this.pollOptions = this.pollOptions.filter((o) => o.id !== id);
  }

  /** Switching type keeps whatever the other mode had, so nothing is lost. */
  setType(type) {
    this.type = type;
    this.panel = null;
  }

  togglePanel(name) {
    this.panel = this.panel === name ? null : name;
  }

  // ── Logic ─────────────────────────────────────────────────────────────────

  // ── Logic ─────────────────────────────────────────────────────────────────

  handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    this.rejected = false;
    this.imageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target.result;
      m.redraw();
    };
    reader.readAsDataURL(file);

    this.screenImage(file);
  }

  /**
   * Screen the picked image for nudity before it can be shared.
   *
   * Runs in the browser, so nothing is uploaded until it passes. A screening
   * failure leaves the image allowed — see nsfwCheck.js.
   */
  screenImage(file) {
    if (!nsfwEnabled()) return;

    this.scanning = true;
    m.redraw();

    checkImage(file).then(({ blocked }) => {
      this.scanning = false;

      if (blocked) {
        this.rejected = true;
        this.imageFile = null;
        this.imagePreview = null;
      }

      m.redraw();
    });
  }

  clearImage() {
    this.imageFile = null;
    this.imagePreview = null;
  }

  /**
   * Check the picked video against the size and length limits before any of
   * it is uploaded. The server checks again; this only spares a wasted upload.
   */
  handleVideoSelect(e) {
    const file = e.target.files[0];
    // Cleared so picking the same file again after a refusal still fires.
    e.target.value = '';
    if (!file) return;

    this.clearVideo();

    checkVideo(file).then(({ error }) => {
      if (error) {
        this.videoError = error;
      } else {
        this.videoFile = file;
        this.videoPreview = URL.createObjectURL(file);
      }

      m.redraw();
    });
  }

  clearVideo() {
    if (this.videoPreview) URL.revokeObjectURL(this.videoPreview);

    this.videoFile = null;
    this.videoPreview = null;
    this.videoError = null;
  }

  canSubmit() {
    if (this.type === 'text') {
      return this.content.trim().length > 0;
    }

    if (this.type === 'video') {
      return this.videoFile !== null;
    }

    // Not while the picture is still being screened, and never once rejected.
    return this.imageFile !== null && !this.scanning;
  }

  submit() {
    this.loading = true;
    this.error = null;

    const data = new FormData();
    data.append('type', this.type);
    data.append('visibility', this.visibility);

    if (this.hasPoll()) {
      data.append('pollQuestion', this.pollQuestion.trim());
      data.append(
        'pollOptions',
        JSON.stringify(this.pollAnswers())
      );
    }

    if (this.type === 'text') {
      data.append('content', this.content.trim());
      data.append('bgGradient', this.bgGradient);
    } else if (this.type === 'video') {
      data.append('video', this.videoFile);
    } else {
      data.append('image', this.imageFile);
    }

    if (this.linkUrl.trim()) {
      data.append('linkUrl', this.linkUrl.trim());

      if (this.linkLabel.trim()) {
        data.append('linkLabel', this.linkLabel.trim());
      }
    }

    app
      .request({
        method: 'POST',
        url: '/api/happydigital/stories',
        serialize: raw => raw, // Don't JSON serialize FormData
        body: data,
        // Handled here instead of by Flarum, whose alert would open behind
        // the composer — the failure looked like nothing happening at all.
        errorHandler: (error) => {
          this.error = this.errorMessage(error);
        },
        // A video takes a while to send; show how far along it is.
        config: (xhr) => {
          if (this.type !== 'video' || !xhr.upload) return;

          this.uploadProgress = 0;
          xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;

            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
            m.redraw();
          };
        },
      })
      .then((response) => {
        this.loading = false;
        this.uploadProgress = null;
        this.attrs.oncreated(response.data, response.canCreate);
      })
      .catch(() => {
        this.loading = false;
        this.uploadProgress = null;
        m.redraw();
      });
  }

  /** The server's own reasons if it gave any, else a generic failure. */
  errorMessage(error) {
    const errors = (error && error.response && error.response.errors) || [];
    const details = errors.map((e) => e.detail).filter(Boolean);

    if (details.length) return details.join(' ');

    // A web server in front of PHP (e.g. nginx client_max_body_size) refuses
    // an oversized body with a bare 413 page, carrying no JSON reasons.
    if (error && error.status === 413) {
      return app.translator.trans('happydigital-stories.forum.video_error_too_large', videoLimits());
    }

    return app.translator.trans('happydigital-stories.forum.submit_failed');
  }
}
